// Client reconciliation of the existing subscription service. This does not set billing amounts.
// Mirrors calculatePlanBilling/getNextBillingPaymentService in subscription.service.js
// and getEffectiveDiscountPercent in discount.helper.js. Keep aligned if those rules change.
// Current server rules (SGD): Professional = 6000/seat; In-house = 1500/seat + 300/extra active company.
export const centsValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    typeof value === "boolean"
  )
    return null;
  const number = Number(value);
  return Number.isSafeInteger(number) ? number : null;
};
export const formatMoney = (cents, currency = "SGD") => {
  if (
    cents === null ||
    cents === undefined ||
    cents === "" ||
    !Number.isFinite(Number(cents))
  )
    return "—";
  try {
    return new Intl.NumberFormat("en-SG", {
      style: "currency",
      currency,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cents) / 100);
  } catch {
    return `${currency} ${(Number(cents) / 100).toFixed(2)}`;
  }
};
export function parseDiscountInput(value) {
  const input = String(value).trim();
  if (!/^\d+(?:\.\d)?$/.test(input)) return null;
  const number = Number(input);
  return number >= 0 && number <= 100 ? number : null;
}
export function effectiveDiscount(subscription) {
  const custom = subscription.custom_discount_percent;
  if (custom !== null && custom !== undefined) {
    const number = Number(custom);
    if (custom === "" || !Number.isFinite(number) || number < 0 || number > 100)
      return { percent: null, source: "Invalid discount" };
    const percent = Math.round(number * 10) / 10;
    const promos = subscription.owner?.redeemed_promo_codes;
    // Redemption history identifies a matching offer, not the origin of a later admin override.
    const matchesPromo =
      Array.isArray(promos) &&
      promos.some(
        (promo) =>
          promo.status === "REDEEMED" &&
          promo.discount_type === "PERCENTAGE" &&
          promo.discount_value != null &&
          Number(promo.discount_value) === percent
      );
    return {
      percent,
      source: matchesPromo
        ? "Subscription discount"
        : "Custom subscription discount",
    };
  }
  const beta = subscription.isOpenBeta ?? subscription.is_open_beta;
  return {
    percent: beta ? 25 : 0,
    source: beta ? "Beta program discount" : "No discount",
  };
}
export const applyDiscount = (cents, percent) =>
  cents == null || percent == null
    ? null
    : Math.round(cents * (1 - percent / 100));

export function getPricing(subscription) {
  const plan = subscription.plan || {};
  const discount = effectiveDiscount(subscription);
  const currency = subscription.currency || plan.currency || "SGD";
  const interval = plan.billing_interval;
  const cycleMonths =
    interval === "monthly" ? 1 : interval === "yearly" ? 12 : null;
  const usersComplete =
    Array.isArray(subscription.users) &&
    (subscription.users_count == null ||
      Number(subscription.users_count) === subscription.users.length);
  const seats = usersComplete
    ? subscription.users.filter(
        (user) =>
          ["admin", "user"].includes(user.user_auth?.user_type) &&
          user.user_auth?.status !== "banned"
      ).length
    : null;
  const companyTotal = centsValue(subscription.companies_count);
  const companyRowsComplete =
    Array.isArray(subscription.companies) &&
    (companyTotal == null || subscription.companies.length === companyTotal);
  const activeCompanies = companyRowsComplete
    ? subscription.companies.filter((company) => company.status === "active")
        .length
    : null;
  const supported = ["Tax Professional", "In-house Tax Practitioner"].includes(
    plan.name
  );
  const seatRate =
    plan.name === "Tax Professional"
      ? 6000
      : plan.name === "In-house Tax Practitioner"
        ? 1500
        : null;
  const seatBase = seats != null && seatRate != null ? seats * seatRate : null;
  const allowance =
    plan.name === "In-house Tax Practitioner" && seats != null
      ? seats * 5
      : null;
  const extraCompanies =
    allowance == null
      ? null
      : activeCompanies != null
        ? Math.max(0, activeCompanies - allowance)
        : companyTotal != null && companyTotal <= allowance
          ? 0
          : null;
  const companyBase =
    plan.name === "Tax Professional"
      ? 0
      : extraCompanies == null
        ? null
        : extraCompanies * 300;
  const base =
    seatBase != null && companyBase != null ? seatBase + companyBase : null;
  const expectedRecurring =
    discount.percent === 100 && supported
      ? 0
      : applyDiscount(base, discount.percent);
  const logs = Array.isArray(subscription.change_logs)
    ? subscription.change_logs
    : [];
  const unpaid = logs.filter((log) => log.status === "unpaid");
  const loadedUnpaid = unpaid.reduce(
    (sum, log) => sum + (centsValue(log.charge_after_discount) ?? 0),
    0
  );
  const missingCharges = unpaid.some(
    (log) =>
      centsValue(log.charge_after_discount) == null ||
      centsValue(log.charge_after_discount) < 0
  );
  const historyComplete =
    Array.isArray(subscription.change_logs) && logs.length < 50;
  const outstanding = historyComplete && !missingCharges ? loadedUnpaid : null;
  const serverTotal = centsValue(subscription.next_payment_cents);
  const derivedRecurring =
    serverTotal != null && outstanding != null && serverTotal >= outstanding
      ? serverTotal - outstanding
      : null;
  const recurring = derivedRecurring ?? expectedRecurring;
  const expectedTotal =
    expectedRecurring != null && outstanding != null
      ? expectedRecurring + outstanding
      : null;
  const reconciled =
    expectedTotal != null && serverTotal != null
      ? expectedTotal === serverTotal
      : null;
  const monthlyEquivalent =
    cycleMonths && recurring != null
      ? recurring / cycleMonths
      : interval === "one_time"
        ? 0
        : null;
  // Explicit panel metric: only active, renewing recurring subscriptions contribute to renewing MRR.
  const mrrEligible =
    subscription.status === "active" &&
    subscription.renewal === true &&
    Boolean(cycleMonths);
  const mrr =
    !subscription.status ||
    (subscription.renewal == null && subscription.status === "active")
      ? null
      : !["monthly", "yearly", "one_time"].includes(interval)
        ? null
        : mrrEligible
          ? monthlyEquivalent
          : 0;
  const catalogRate = centsValue(plan.price_cents_per_user);
  return {
    currency,
    interval,
    cycleMonths,
    discount,
    seats,
    seatRate,
    seatBase,
    allowance,
    activeCompanies,
    companyTotal,
    extraCompanies,
    companyBase,
    base,
    expectedRecurring,
    recurring,
    derivedRecurring,
    serverTotal,
    outstanding,
    loadedUnpaid,
    missingCharges,
    historyComplete,
    unpaid,
    expectedTotal,
    reconciled,
    monthlyEquivalent,
    mrr,
    mrrEligible,
    supported,
    catalogMismatch:
      catalogRate != null && seatRate != null && catalogRate !== seatRate,
  };
}

export function checkInvoice(log) {
  const base = centsValue(log.extra_fee_cents);
  const net = centsValue(log.charge_after_discount);
  const percent = log.discount == null ? null : Number(log.discount);
  const validRate =
    percent != null &&
    Number.isFinite(percent) &&
    percent >= 0 &&
    percent <= 100;
  const expected = validRate ? applyDiscount(base, percent) : null;
  return {
    base,
    net,
    percent: validRate ? percent : null,
    expected,
    matches: expected != null && net != null ? expected === net : null,
  };
}
